import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { load } from 'ember-async-data';
import cannot from 'ember-can/helpers/cannot';
import { t } from 'ember-intl';
import momentFormat from 'ember-moment/helpers/moment-format';
import { or } from 'ember-truth-helpers';
import { trackedFunction } from 'reactiveweb/function';

import styles from 'umg-ember-backend/components/ui/form/fieldset/styles.module.css';
import LinkToDetail from 'umg-ember-backend/components/ui/link-to-detail';
import { findRecord } from 'umg-ember-backend/data/builders/umg-rest';
import { getBem } from 'umg-ember-backend/utils/get-bem';

const bem = getBem(styles);

export default class UiFormSectionsMetaComponent extends Component {
  // Services
  @service currentUser;
  @service intl;
  @service store;

  // Defaults
  @tracked isClosed = true;

  // Getter and setter
  get historyEntries() {
    return this.createHistory.value ?? [];
  }

  createHistory = trackedFunction(this, async () => {
    const rawEntries = this.args.model.cmsHistory.entrys;

    if (!rawEntries) {
      return [];
    }
    return rawEntries
      .slice()
      .sort((a, b) => a.timestamp > b.timestamp)
      .reverse()
      .slice(0, 20)
      .map((entry) => {
        let { timestamp, modifier_id, note } = entry;
        let modifier;

        if (modifier_id) {
          const modifierId = String(modifier_id);

          modifier = this.store.peekRecord({ type: 'user', id: modifierId });

          if (!modifier) {
            modifier = this.store
              .request(findRecord('user', modifierId))
              .then(({ content }) => content.data);
          }
        }

        return {
          timestamp,
          modifier,
          note: this.intl.t(`meta_box.history.note.${note}`),
        };
      });
  });

  // Actions
  toggleMetaBox = () => {
    this.isClosed = !this.isClosed;
  };

  // Template
  <template>
    {{#unless (or @model.isNew (cannot 'viewMetaData' @model))}}
      <div
        class={{bem (hash is-closed=this.isClosed)}}
        data-selector-ui-form-sections-meta
        ...attributes
      >
        <div
          class={{bem 'legend'}}
          role='button'
          {{on 'click' this.toggleMetaBox}}
        >
          {{t 'meta_box.headline'}}
        </div>
        <div class={{bem 'content'}}>
          {{#if (has-block)}}
            <div data-test-ui-form-section-meta-has-yield>
              {{yield}}
            </div>
          {{/if}}

          {{#if @model.cmsOwnerId}}
            <label>
              {{t 'meta_box.labels.cmsOwnerId'}}
            </label>
            <div class='u-margin-bottom-small'>
              {{@model.cmsOwnerId.name}}
            </div>
          {{/if}}

          <label>
            {{t 'meta_box.labels.cmsCreatorId'}}
          </label>
          <div class='u-margin-bottom-small'>
            {{@model.cmsCreatorId.name}}
          </div>

          <label>
            {{t 'meta_box.labels.cmsCreated'}}
          </label>
          <div class='u-margin-bottom-small'>
            {{momentFormat @model.cmsCreated 'L LT'}}
          </div>

          <label>
            {{t 'meta_box.labels.cmsModifierId'}}
          </label>
          <div class='u-margin-bottom-small'>
            {{@model.cmsModifierId.name}}
          </div>

          <label>
            {{t 'meta_box.labels.cmsModified'}}
          </label>
          <div class='u-margin-bottom-small'>
            {{momentFormat @model.cmsModified 'L LT'}}
          </div>

          <label>
            {{t 'meta_box.labels.cmsLocktime'}}
          </label>
          <div class='u-margin-bottom-small'>
            {{#if @model.cmsLocktime}}
              {{momentFormat @model.cmsLocktime 'L LT'}}
            {{else}}
              -
            {{/if}}
          </div>

          <label>
            {{t 'meta_box.labels.cmsLockerId'}}
          </label>
          <div class='u-margin-bottom-small'>
            {{if @model.cmsLockerId.name @model.cmsLockerId.name '-'}}
          </div>

          {{#if @model.cmsHistory}}
            <div data-test-ui-form-sections-meta-cms-history>
              <table class='o-table'>
                <thead class='o-table__head'>
                  <tr class='o-table__row'>
                    <th class='o-table__cell u-1/3'>
                      {{t 'meta_box.history.header.modifier'}}
                    </th>
                    <th class='o-table__cell u-1/3'>
                      {{t 'meta_box.history.header.note'}}
                    </th>
                    <th class='o-table__cell u-1/3 u-align-right'>
                      {{t 'meta_box.history.header.timestamp'}}
                    </th>
                  </tr>
                </thead>
                <tbody class='o-table__body'>
                  {{#each this.historyEntries as |entry|}}
                    <tr class='o-table__row'>
                      <td class='o-table__cell'>
                        {{#let (load entry.modifier) as |result|}}
                          {{#if result.isResolved}}
                            {{#if result.value}}
                              <LinkToDetail @model={{result.value}}>
                                {{result.value.name}}
                              </LinkToDetail>
                            {{/if}}
                          {{else if result.isPending}}
                            {{t 'ui.loading'}}
                          {{/if}}
                        {{/let}}
                      </td>
                      <td class='o-table__cell'>{{entry.note}}</td>
                      <td class='o-table__cell u-align-right'>
                        {{momentFormat entry.timestamp 'L LT' 'X'}}
                      </td>
                    </tr>
                  {{/each}}
                </tbody>
              </table>
            </div>
          {{/if}}
        </div>
      </div>
    {{/unless}}
  </template>
}
